/* The battle board, exactly as the server deals it (simulator/use_cases/battlefield.py).

Zones of slots filled in zone order from the side's per-zone priority lists — default cheapest
first — with everyone who does not fit waiting in reserve. Mirrored here so the battle view and the
formation editor show the same field the engine fights on.

One board per *team* (2026-08-21): allies pool their armies into the same slots — mixed types,
mixed owners. It does not widen with the company (2026-09-10): three allies get one player's
frontage and queue the rest in reserve. */


export const ZONE_ORDER = ["front", "flanks", "back", "special"]

// Which enemy zone each of ours strikes, first to last (owner, 2026-09-13). An attacker takes the
// first zone with anyone standing in it. Mirrors `TARGET_ORDER` in simulator/use_cases/resolve_combat.py;
// the zone help shows it so a player knows who a slot will hit.
export const TARGET_ORDER = {
  front: ["front", "flanks", "back", "special"],
  flanks: ["flanks", "back", "special", "front"],
  back: ["front", "flanks", "back", "special"],
  special: ["front", "flanks", "back", "special"],
}

// The owner's placement tables: who may stand where is decided per *unit* and per side, in the
// owner's order — which is also the default priority. A unit absent from a zone's list cannot
// stand there. Mirrors `simulator/use_cases/battlefield.py`; the server is the gate, this is what
// the editor and the unit sheet show.
//
// Humans (P6, 2026-09-01). The medic joined the front line on 2026-09-19 (owner's hotfix) as the
// LAST priority: it seats only where the soldiers, elites and workers have run out, and nowhere
// else.
export const HUMAN_ZONE_UNITS = {
  front: ["soldier", "elite", "worker", "medic"],
  flanks: ["soldier", "elite"],
  back: ["ranger"],
  special: ["tank", "helicopter"],
}

// Zombies (Z1, 2026-09-06). The destructor was not in the owner's table; it keeps its siege zones
// after the owner's picks — see the server's note.
export const ZOMBIE_ZONE_UNITS = {
  front: ["zombie", "walker", "ambusher", "dog"],
  flanks: ["dog", "zombie", "ambusher"],
  // The destructor stands in `special` and nowhere else (owner, 2026-09-14, answering Z1a): two
  // slots rather than the rearguard's five, on the screened side of the board, which is what a
  // siege engine you send *at a wall* gets instead of a seat in every stack.
  back: ["cursed"],
  special: ["titan", "destructor"],
}

const ZONE_UNITS = { human: HUMAN_ZONE_UNITS, zombie: ZOMBIE_ZONE_UNITS }

const troopsFor = (gameConfig, team) => gameConfig?.[team === "human" ? "human_config" : "zombie_config"]?.TROOPS || {}

/* Every unit a side can field, in the server's own order — the roster the formation modal lists
   so a player can open any unit's sheet, their own side's and the enemy's, before a battle asks
   them to (owner, 2026-09-20). Read off the config rather than off the zone tables, because a
   unit that stands nowhere is still a unit somebody can meet. */
export const rosterFor = (gameConfig, team) => Object.keys(troopsFor(gameConfig, team))

export const eligibleUnits = (gameConfig, team, zone) => {
  const troops = troopsFor(gameConfig, team)
  return (ZONE_UNITS[team]?.[zone] || []).filter((name) => troops[name])
}

/* Where one unit may stand, in board order — the unit sheet's "puede ocupar" line. */
export const zonesFor = (gameConfig, team, unit) =>
  ZONE_ORDER.filter((zone) => eligibleUnits(gameConfig, team, zone).includes(unit))

// Where a unit stands, in words, for a caption. It replaced the role label (2026-09-13): the role's
// names copy the zones' ("Retaguardia" for siege) but the role has not placed a unit since Z1, so
// a destructor that stands only in the special cells was captioned as rearguard.
export const zoneNames = (t, gameConfig, team, unit) =>
  zonesFor(gameConfig, team, unit)
    .map((zone) => t(`game.battle.zones.${zone}`))
    .join(" · ")

export const zonePriorities = (gameConfig, team, formation) => {
  const priorities = {}
  for (const zone of ZONE_ORDER) {
    const eligible = eligibleUnits(gameConfig, team, zone)
    const chosen = []
    for (const name of formation?.[zone] || []) {
      if (eligible.includes(name) && !chosen.includes(name)) chosen.push(name)
    }
    priorities[zone] = [...chosen, ...eligible.filter((name) => !chosen.includes(name))]
  }
  return priorities
}

/* Mirrors `simulator/use_cases/battlefield.slot_size`: the game's round alone sizes a slot, in a
   city and on open ground alike — max(SLOT_SIZE_MIN, SLOT_SIZE_PER_ROUND × round), so round 10
   seats 100 a slot and round 0 the floor of 10 rather than an empty board (owner, 2026-09-29).
   It replaced the city's main-building rule and the field's five-round steps. */
export const slotSizeFor = (rules, round = 0) =>
  Math.max(
    rules?.BATTLEFIELD_SLOT_SIZE_MIN ?? 0,
    (rules?.BATTLEFIELD_SLOT_SIZE_PER_ROUND ?? 0) * Math.max(0, Math.floor(round || 0)),
  )

export const boardCapacity = (rules, slotSize) =>
  slotSize * Object.values(rules?.BATTLEFIELD_ZONE_SLOTS || {}).reduce((total, slots) => total + slots, 0)

/* available: {unitType: count} pooled across the whole side. Returns
   {board: {zone: [slot]}, reserves: [{unit, count}]} where a slot is an array of {unit, count} —
   a slot may hold several unit types since the board became the team's. */
export const fillBoard = ({ available, gameConfig, team, formation, slotSize, rules }) => {
  const remaining = {}
  for (const [unit, count] of Object.entries(available)) if (count > 0) remaining[unit] = count
  const priorities = zonePriorities(gameConfig, team, formation)
  const zoneSlots = rules?.BATTLEFIELD_ZONE_SLOTS || {}
  const board = {}
  for (const zone of ZONE_ORDER) {
    const slots = []
    for (let index = 0; index < (zoneSlots[zone] ?? 0); index += 1) {
      const slot = []
      let room = slotSize
      for (const unit of priorities[zone]) {
        if (room <= 0) break
        const take = Math.min(room, remaining[unit] || 0)
        if (take > 0) {
          slot.push({ unit, count: take })
          remaining[unit] -= take
          room -= take
        }
      }
      slots.push(slot)
    }
    board[zone] = slots
  }
  const reserves = Object.entries(remaining)
    .filter(([, count]) => count > 0)
    .map(([unit, count]) => ({ unit, count }))
  return { board, reserves }
}
